import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Mail, Phone, MapPin } from 'lucide-react';

const footerLinks = {
  platform: [
    { label: 'Features', to: '/features' },
    { label: 'How It Works', to: '/how-it-works' },
    { label: 'Pricing', to: '/pricing' },
    { label: 'Book a Demo', to: '/contact' },
  ],
  useCases: [
    { label: 'Solo Practices', to: '/use-cases/therapists' },
    { label: 'Small to Mid-Size Practices', to: '/use-cases/clinics' },
    { label: 'Large Group Practices', to: '/use-cases/group-practices' },
    { label: 'APCM Services', to: '/apcm' },
  ],
  company: [
    { label: 'About Us', to: '/about' },
    { label: 'Testimonials', to: '/testimonials' },
    { label: 'Contact', to: '/contact' },
    { label: 'FAQ', to: '/faq' },
  ],
  access: [
    { label: 'Provider Login', href: 'https://app.doctorite.ai', external: true as const },
    { label: 'Client Login', href: 'https://my.doctorite.ai', external: true as const },
    { label: 'Start Free Trial', href: '/register', external: false as const },
  ],
  legal: [
    { label: 'Privacy Policy', to: '/privacy-policy' },
    { label: 'Terms of Use', to: '/terms-of-use' },
    { label: 'HIPAA Compliance', to: '/hipaa-compliance' },
    { label: 'Security', to: '/security' },
    { label: 'Disclaimer', to: '/disclaimer' },
  ],
};

const linkClass = 'text-sm text-brand-300/70 hover:text-white transition-colors';

export default function Footer() {
  return (
    <footer className="bg-brand-950 text-white">
      <div className="container-max section-padding !pb-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12">
          {/* Brand */}
          <div className="lg:col-span-3">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <img
                src="/favicon-icon.png"
                alt="Doctorite"
                className="h-8 w-auto object-contain"
              />
              <span className="text-lg sm:text-xl font-semibold tracking-tight text-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                Doctorite
              </span>
            </Link>
            <p className="text-sm text-brand-300/70 leading-relaxed mb-6 max-w-xs">
              The AI-powered behavioral health platform that thinks with you.
              Empowering counselors, clinics, and organizations with clinical intelligence.
            </p>

            <div className="space-y-2">
              <a href="tel:+16823735380" className="flex items-center gap-2 text-sm text-brand-300/70 hover:text-white transition-colors">
                <Phone className="w-4 h-4" />
                +1 (682) 373-5380
              </a>
              <a href="mailto:hello@doctorite.ai" className="flex items-center gap-2 text-sm text-brand-300/70 hover:text-white transition-colors">
                <Mail className="w-4 h-4" />
                hello@doctorite.ai
              </a>
              <div className="flex items-start gap-2 text-sm text-brand-300/70">
                <MapPin className="w-4 h-4 shrink-0 mt-0.5" />
                <span>18240 Midway Road, Dallas TX 75287</span>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <a
                href="https://www.linkedin.com/company/doctorite/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/20 transition-colors"
                aria-label="Doctorite on LinkedIn"
              >
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div className="lg:col-span-9 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8">
            <div>
              <h4 className="text-sm font-bold text-white mb-4 uppercase tracking-wider">Platform</h4>
              <ul className="space-y-2.5">
                {footerLinks.platform.map((link) => (
                  <li key={link.to}>
                    <Link to={link.to} className={linkClass}>{link.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-bold text-white mb-4 uppercase tracking-wider">Solutions</h4>
              <ul className="space-y-2.5">
                {footerLinks.useCases.map((link) => (
                  <li key={link.to}>
                    <Link to={link.to} className={linkClass}>{link.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-bold text-white mb-4 uppercase tracking-wider">Company</h4>
              <ul className="space-y-2.5">
                {footerLinks.company.map((link) => (
                  <li key={link.to}>
                    <Link to={link.to} className={linkClass}>{link.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-bold text-white mb-4 uppercase tracking-wider">Access</h4>
              <ul className="space-y-2.5">
                {footerLinks.access.map((link) => (
                  <li key={link.href}>
                    {link.external ? (
                      <a href={link.href} target="_blank" rel="noopener noreferrer" className={linkClass}>{link.label}</a>
                    ) : (
                      <Link to={link.href} className={linkClass}>{link.label}</Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-bold text-white mb-4 uppercase tracking-wider">Legal</h4>
              <ul className="space-y-2.5">
                {footerLinks.legal.map((link) => (
                  <li key={link.to}>
                    <Link to={link.to} className={linkClass}>{link.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container-max px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-xs text-brand-300/50">
            &copy; {new Date().getFullYear()} Doctorite AI. All rights reserved.
          </p>
          <p className="text-xs text-brand-300/50">
            HIPAA Compliant &middot; End-to-End Encrypted &middot; CMS BHI Certified
          </p>
        </div>
      </div>
    </footer>
  );
}
